import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Clock, ShoppingCart } from 'lucide-react';
import flashDealService from '../../services/flashDealService';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { applyFlashDiscount } from '../../lib/variantUnits';

export function useCountdown(endsAt) {
  const calc = () => {
    if (!endsAt) return 0;
    let target = endsAt;
    if (typeof target === 'string') {
      if (!target.endsWith('Z') && !/[-+]\d{2}:?\d{2}$/.test(target)) {
        target = target.includes('T') ? `${target}Z` : `${target.replace(' ', 'T')}Z`;
      }
    }
    return Math.max(0, new Date(target) - Date.now());
  };
  const [ms, setMs] = useState(calc);
  useEffect(() => {
    const t = setInterval(() => setMs(calc()), 1000);
    return () => clearInterval(t);
  });
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return { h, m, s, expired: ms === 0 };
}

export function CountdownBadge({ endsAt, className = "text-orange-600" }) {
  const { h, m, s, expired } = useCountdown(endsAt);
  if (expired) return <span className="text-xs text-gray-400">Expired</span>;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-mono font-semibold ${className}`}>
      <Clock className="w-3 h-3" />
      {h > 0 && `${h}h `}{String(m).padStart(2, '0')}m {String(s).padStart(2, '0')}s
    </span>
  );
}

function DealCard({ deal, onAddToCart, onSelect }) {
  const img = deal.product?.images?.[0]?.imageUrl;
  const original = Number(deal.product?.pricePerUnit ?? 0);
  const flash = Number(deal.discountedPrice);

  // Variant products have no single price here — show the discounted range and
  // send the customer to the detail modal to pick a size (we can't blindly add
  // a variant-less line at the base discount).
  const variants = deal.product?.variants ?? [];
  const hasVariants = variants.length > 0;
  // Prefer the server-computed per-variant flash price (authoritative — handles
  // flat deals correctly); fall back to the client percent calc for older payloads.
  const variantFlashPrices = hasVariants
    ? variants.map(v => v.flashDealPrice != null
        ? Number(v.flashDealPrice)
        : applyFlashDiscount(Number(v.pricePerUnit ?? v.price ?? 0), deal.discountPercent))
    : [];
  const minFlash = hasVariants ? Math.min(...variantFlashPrices) : flash;
  const maxFlash = hasVariants ? Math.max(...variantFlashPrices) : flash;

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-orange-100 hover:shadow-lg transition-shadow flex flex-col">
      <div className="relative">
        {img ? (
          <img src={img} alt={deal.product.name} className="w-full h-40 object-cover" />
        ) : (
          <div className="w-full h-40 bg-gray-100 flex items-center justify-center text-gray-400 text-sm">No image</div>
        )}
        <div className="absolute top-2 left-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
          <Zap className="w-3 h-3" /> {deal.discountPercent}% OFF
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <p className="font-semibold text-gray-800 mb-1 line-clamp-2">{deal.product?.name}</p>

        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-xl font-bold text-green-700">
            {hasVariants && minFlash !== maxFlash
              ? `₹${minFlash} – ₹${maxFlash}`
              : `₹${minFlash}`}
          </span>
          {!hasVariants && (
            <span className="text-sm text-gray-400 line-through">₹{original}</span>
          )}
        </div>

        <div className="mb-3">
          <CountdownBadge endsAt={deal.endsAt} />
        </div>

        {deal.maxQuantity && (
          <p className="text-xs text-gray-500 mb-2">
            {/* quantitySold can be fractional (sub-base variants); floor so we
                never advertise more than a whole unit's worth as available. */}
            {Math.max(0, Math.floor(deal.maxQuantity - deal.quantitySold))} of {deal.maxQuantity} left
          </p>
        )}

        {hasVariants ? (
          <button
            onClick={() => onSelect(deal)}
            className="mt-auto w-full flex items-center justify-center gap-2 bg-green-700 hover:bg-green-800 text-white py-2 rounded-xl text-sm font-medium transition-colors"
          >
            <ShoppingCart className="w-4 h-4" /> Select Size
          </button>
        ) : (
          <button
            onClick={() => onAddToCart(deal)}
            className="mt-auto w-full flex items-center justify-center gap-2 bg-green-700 hover:bg-green-800 text-white py-2 rounded-xl text-sm font-medium transition-colors"
          >
            <ShoppingCart className="w-4 h-4" /> Add to Cart
          </button>
        )}
      </div>
    </div>
  );
}

export default function FlashDealsSection() {
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const data = await flashDealService.getActive();
      setDeals(data);
    } catch {
      // silently fail — non-critical section
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  if (!isAuthenticated || (!loading && deals.length === 0)) return null;

  return (
    <section className="py-16 bg-gradient-to-br from-orange-50 to-yellow-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-3">
            <Zap className="w-4 h-4" /> Limited Time Offers
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800">Flash Deals</h2>
          <p className="text-gray-500 mt-2">Fresh produce at discounted prices — grab them before they're gone!</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-md h-72 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {deals.map((deal) => (
              <DealCard
                key={deal.id}
                deal={deal}
                onAddToCart={(d) => addToCart({
                  id: d.flashSku ?? d.productId,
                  productId: d.productId,
                  name: d.product?.name,
                  price: Number(d.discountedPrice),
                  originalPrice: Number(d.product?.pricePerUnit ?? d.discountedPrice),
                  flashSku: d.flashSku ?? null,
                  image: d.product?.images?.[0]?.imageUrl || 'https://via.placeholder.com/200',
                  weight: d.product?.unitType || 'Grams',
                })}
                onSelect={(d) => navigate('/shop', { state: { openProduct: d.productId } })}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
